import axios from 'axios'

/**
 * F1.11: Soft redirect that uses history.pushState when possible, so the
 * React Router state is preserved (avoids losing scroll position and
 * unmounting/mounting the whole tree on 401).
 */
function redirectTo(path: string) {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === path) return;
  // Use history API so the React Router layer intercepts it.
  window.history.pushState({}, '', path);
  // Pop a state event so React Router picks it up; also fallback to a
  // full reload if no router picks it up.
  window.dispatchEvent(new PopStateEvent('popstate'));
  // Some routers (incl. react-router-dom v6) need an explicit navigation
  // trigger. Use a custom event the app's nav wrapper can listen to.
  window.dispatchEvent(new CustomEvent('app:redirect', { detail: { path } }));
}

const api = axios.create({
  baseURL: (import.meta as any).env.PROD ? '/api' : 'http://localhost:3000/api',
  withCredentials: true, // F1.10: Send httpOnly cookies (auth_token, refresh_token)
  headers: {
    'Content-Type': 'application/json',
  },
})

let isRefreshing = false
let refreshSubscribers: Array<() => void> = []

function onRefreshed() {
  refreshSubscribers.forEach((cb) => cb())
  refreshSubscribers = []
}

function addRefreshSubscriber(cb: () => void) {
  refreshSubscribers.push(cb)
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const path = window.location.pathname

    const isTokenError = error.response?.status === 401 ||
      (error.response?.status === 403 &&
        (error.response?.data?.error?.includes('Invalid or expired token') ||
         error.response?.data?.message?.includes('Invalid or expired token') ||
         error.response?.data?.error?.includes('Geçersiz token') ||
         error.response?.data?.message?.includes('Invalid admin token') ||
         error.response?.data?.message?.includes('Admin yetkileri gerekli') ||
         error.response?.data?.message?.includes('Moderator privileges required')))

    if (!isTokenError || originalRequest._retry) {
      return Promise.reject(error)
    }

    // Don't redirect if already on login pages or if the request itself is an auth probe
    const requestUrl = originalRequest.url || ''
    if (path === '/login' || path === '/register' || path === '/admin/login' || path === '/moderator/login') {
      return Promise.reject(error)
    }
    if (requestUrl === '/auth/me' || requestUrl.endsWith('/auth/me')) {
      return Promise.reject(error)
    }

    // Check if we have a dedicated admin/moderator token (those don't refresh)
    const requestPath = originalRequest.url || ''

    // F4.7: Admin/moderator sessions live in httpOnly cookies; they don't use refresh tokens.
    // If the request fails on an admin/moderator path, redirect to the panel login directly.
    const isAdminOrModContext =
      path.startsWith('/admin') || path.startsWith('/moderator') ||
      requestPath.startsWith('/admin') || requestPath.startsWith('/moderator') ||
      requestPath.startsWith('/recovery-stories/admin')

    if (isAdminOrModContext) {
      if (path.startsWith('/admin') || requestPath.startsWith('/admin')) {
        redirectTo('/admin/login')
      } else {
        redirectTo('/moderator/login')
      }
      return Promise.reject(error)
    }

    // Normal users: try refresh via httpOnly cookie
    if (!isRefreshing) {
      isRefreshing = true
      originalRequest._retry = true

      try {
        // Refresh token is stored in an httpOnly cookie by the backend.
        await axios.post(
          (import.meta as any).env.PROD ? '/api/auth/refresh' : 'http://localhost:3000/api/auth/refresh',
          {},
          { withCredentials: true }
        )

        // Cookie is automatically updated by the backend; no need to store anything in localStorage.
        onRefreshed()
        isRefreshing = false

        return api(originalRequest)
      } catch (refreshError) {
        isRefreshing = false
        // If on admin/moderator route, go to respective login
        if (path.startsWith('/admin')) {
          redirectTo('/admin/login')
        } else if (path.startsWith('/moderator')) {
          redirectTo('/moderator/login')
        } else {
          redirectTo('/login')
        }
        return Promise.reject(refreshError)
      }
    }

    // Wait for token refresh and retry
    return new Promise((resolve) => {
      addRefreshSubscriber(() => {
        resolve(api(originalRequest))
      })
    })
  }
)

export default api
